// Incident Report Form Template
// Based on SOP section 11.2 Incident Reporting

const getIncidentReportFormTemplate = (t) => ({
  formType: 'incident-report',
  title: {
    en: 'Incident Report Form',
    th: 'แบบรายงานเหตุการณ์'
  },
  description: {
    en: 'Standardized form for documenting workplace incidents, accidents, and near misses',
    th: 'แบบฟอร์มมาตรฐานสำหรับบันทึกเหตุการณ์อุบัติเหตุและเหตุการณ์ใกล้พลาดในที่ทำงาน'
  },
  fields: [
    // Basic Incident Information
    {
      name: 'incidentDate',
      label: {
        en: 'Incident Date & Time',
        th: 'วันที่และเวลาเกิดเหตุการณ์'
      },
      type: 'datetime-local',
      required: true,
      tooltip: {
        en: 'Exact date and time when the incident occurred. Use 24-hour format. Example: 2023-10-15 14:30',
        th: 'วันที่และเวลาที่แน่นอนที่เกิดเหตุการณ์ ใช้รูปแบบ 24 ชั่วโมง ตัวอย่าง: 2023-10-15 14:30'
      }
    },
    {
      name: 'reportDate',
      label: {
        en: 'Report Date & Time',
        th: 'วันที่และเวลาที่รายงาน'
      },
      type: 'datetime-local',
      required: true,
      tooltip: {
        en: 'Date and time when this incident report was created. Example: 2023-10-15 15:45',
        th: 'วันที่และเวลาที่สร้างรายงานเหตุการณ์นี้ ตัวอย่าง: 2023-10-15 15:45'
      }
    },
    {
      name: 'reporterName',
      label: {
        en: 'Reporter Name',
        th: 'ชื่อผู้รายงาน'
      },
      type: 'text',
      required: true,
      tooltip: {
        en: 'Full name of the person reporting the incident. Example: Sarah Johnson',
        th: 'ชื่อ-นามสกุลของผู้รายงานเหตุการณ์ ตัวอย่าง: ซาราห์ จอห์นสัน'
      }
    },
    {
      name: 'reporterPosition',
      label: {
        en: 'Reporter Position',
        th: 'ตำแหน่งของผู้รายงาน'
      },
      type: 'text',
      required: true,
      tooltip: {
        en: 'Job title or role of the reporter. Example: Field Technician Level 2',
        th: 'ตำแหน่งงานหรือบทบาทของผู้รายงาน ตัวอย่าง: ช่างเทคนิคในสนาม ระดับ 2'
      }
    },
    {
      name: 'incidentLocation',
      label: {
        en: 'Incident Location',
        th: 'สถานที่เกิดเหตุการณ์'
      },
      type: 'text',
      required: true,
      tooltip: {
        en: 'Specific location where the incident occurred. Include field section, building, or equipment identifier. Example: Field Block C - Near Irrigation Pump #3',
        th: 'สถานที่เฉพาะที่เกิดเหตุการณ์ รวมถึงส่วนของพื้นที่ อาคาร หรือรหัสอุปกรณ์ ตัวอย่าง: พื้นที่บล็อก C - ใกล้ปั๊มน้ำรดน้ำ #3'
      }
    },
    {
      name: 'incidentType',
      label: {
        en: 'Incident Type',
        th: 'ประเภทของเหตุการณ์'
      },
      type: 'select',
      options: [
        { value: 'safety', label: { en: 'Safety Incident', th: 'เหตุการณ์ด้านความปลอดภัย' } },
        { value: 'environmental', label: { en: 'Environmental Incident', th: 'เหตุการณ์ด้านสิ่งแวดล้อม' } },
        { value: 'quality', label: { en: 'Quality Incident', th: 'เหตุการณ์ด้านคุณภาพ' } },
        { value: 'regulatory', label: { en: 'Regulatory Incident', th: 'เหตุการณ์ด้านกฎหมาย' } },
        { value: 'security', label: { en: 'Security Incident', th: 'เหตุการณ์ด้านความมั่นคง' } },
        { value: 'near-miss', label: { en: 'Near Miss', th: 'เหตุการณ์ใกล้พลาด' } },
        { value: 'other', label: { en: 'Other', th: 'อื่น ๆ' } }
      ],
      required: true,
      tooltip: {
        en: 'Category that best describes the type of incident. Safety: Injuries or threats to personnel. Environmental: Pollution or ecological damage. Near Miss: Event that almost caused harm.',
        th: 'หมวดหมู่ที่อธิบายประเภทของเหตุการณ์ได้ดีที่สุด ความปลอดภัย: การบาดเจ็บหรือภัยคุกคามต่อบุคลากร สิ่งแวดล้อม: มลพิษหรือความเสียหายทางนิเวศ ใกล้พลาด: เหตุการณ์ที่เกือบจะทำให้เกิดอันตราย'
      }
    },

    // People Involved Section
    {
      name: 'sectionHeader_people',
      label: {
        en: '=== People Involved ===',
        th: '=== บุคคลที่เกี่ยวข้อง ==='
      },
      type: 'header'
    },
    {
      name: 'injuredPersonnel',
      label: {
        en: 'Injured Personnel (if any)',
        th: 'บุคลากรที่ได้รับบาดเจ็บ (หากมี)'
      },
      type: 'textarea',
      rows: 3,
      placeholder: {
        en: 'Name, position, injury description...',
        th: 'ชื่อ ตำแหน่ง ลักษณะการบาดเจ็บ...'
      },
      tooltip: {
        en: 'List all individuals injured in the incident. Include: Full name, job title, department, nature of injury, body part affected, treatment provided. Example: John Smith, Field Technician, Right hand cut, Superficial laceration, First aid applied.',
        th: 'ระบุบุคคลทั้งหมดที่ได้รับบาดเจ็บในเหตุการณ์ รวมถึง: ชื่อ-นามสกุล ตำแหน่งงาน แผนก ลักษณะการบาดเจ็บ ส่วนของร่างกายที่ได้รับผล วิธีการรักษาที่ให้ ตัวอย่าง: จอห์น สมิธ ช่างเทคนิคในสนาม ตัดมือขวา แผลฉีกผิวหนัง ให้ปฐมพยาบาล'
      }
    },
    {
      name: 'witnesses',
      label: {
        en: 'Witnesses',
        th: 'พยาน'
      },
      type: 'textarea',
      rows: 3,
      placeholder: {
        en: 'Name, position of witnesses...',
        th: 'ชื่อ ตำแหน่งของพยาน...'
      },
      tooltip: {
        en: 'List all individuals who witnessed the incident. Include contact information for follow-up interviews. Example: Mary Johnson (mary.johnson@company.com), Safety Observer',
        th: 'ระบุบุคคลทั้งหมดที่เป็นพยานเหตุการณ์ รวมถึงข้อมูลการติดต่อสำหรับการสัมภาษณ์ติดตาม ตัวอย่าง: แมรี่ จอห์นสัน (mary.johnson@company.com), เจ้าหน้าที่สังเกตความปลอดภัย'
      }
    },

    // Incident Description Section
    {
      name: 'sectionHeader_description',
      label: {
        en: '=== Incident Description ===',
        th: '=== คำอธิบายเหตุการณ์ ==='
      },
      type: 'header'
    },
    {
      name: 'incidentSequence',
      label: {
        en: 'Sequence of Events',
        th: 'ลำดับเหตุการณ์'
      },
      type: 'textarea',
      rows: 6,
      placeholder: {
        en: 'Describe exactly what happened leading up to, during, and after the incident...',
        th: 'อธิบายสิ่งที่เกิดขึ้นอย่างละเอียดก่อน ขณะ และหลังจากเกิดเหตุการณ์...'
      },
      required: true,
      tooltip: {
        en: 'Chronologically detail events leading to the incident. Start 30 minutes before, describe the incident itself, then immediate aftermath. Include environmental conditions, equipment status, and personnel actions. Example: 14:00 - Worker began pesticide application. 14:15 - Wind direction shifted unexpectedly. 14:16 - Spray drift affected adjacent field.',
        th: 'ระบุรายละเอียดเหตุการณ์ตามลำดับเวลาที่นำไปสู่เหตุการณ์ เริ่มจาก 30 นาทีก่อน อธิบายเหตุการณ์เอง จากนั้นผลกระทบในทันที รวมถึงสภาวะแวดล้อม สถานะอุปกรณ์ และการดำเนินการของบุคลากร ตัวอย่าง: 14:00 - ผู้ปฏิบัติงานเริ่มใช้สารกำจัดศัตรูพืช 14:15 - ทิศทางลมเปลี่ยนแปลงอย่างไม่คาดคิด 14:16 - ละอองสารส่งผลต่อพื้นที่ใกล้เคียง'
      }
    },
    {
      name: 'rootCause',
      label: {
        en: 'Root Cause Analysis',
        th: 'การวิเคราะห์สาเหตุรากฐาน'
      },
      type: 'textarea',
      rows: 4,
      placeholder: {
        en: 'What was the underlying cause of this incident?',
        th: 'สาเหตุรากฐานของเหตุการณ์นี้คืออะไร?'
      },
      tooltip: {
        en: 'Identify the fundamental reason the incident occurred. Use the "5 Whys" technique. Example: Why did the worker slip? Because the floor was wet. Why was the floor wet? Because of a leaky pipe. Continue until you reach the systemic issue.',
        th: 'ระบุเหตุผลพื้นฐานที่เหตุการณ์เกิดขึ้น ใช้เทคนิค "ทำไม 5 ครั้ง" ตัวอย่าง: ทำไมผู้ปฏิบัติงานลื่น? เพราะพื้นเปียก ทำไมพื้นเปียก? เพราะท่อรั่ว ทำต่อไปจนกว่าจะไปถึงปัญหาระบบ'
      }
    },
    {
      name: 'contributingFactors',
      label: {
        en: 'Contributing Factors',
        th: 'ปัจจัยที่มีส่วนเกี่ยวข้อง'
      },
      type: 'textarea',
      rows: 4,
      placeholder: {
        en: 'List any contributing factors (weather, equipment condition, human factors, etc.)...',
        th: 'ระบุปัจจัยที่มีส่วนเกี่ยวข้อง (สภาพอากาศ สภาพอุปกรณ์ ปัจจัยมนุษย์ เป็นต้น)...'
      },
      tooltip: {
        en: 'Identify environmental, procedural, or human factors that increased incident likelihood. Example: Poor lighting contributed to trip hazard. Lack of recent safety refresher training reduced awareness.',
        th: 'ระบุสิ่งแวดล้อม ขั้นตอน หรือปัจจัยมนุษย์ที่เพิ่มโอกาสเกิดเหตุการณ์ ตัวอย่าง: แสงสว่างไม่เพียงพ contribute ต่ออันตรายการสะดุด ขาดการฝึกอบรมความปลอดภัยล่าสุดทำให้ลดความตระหนัก'
      }
    },

    // Immediate Response Section
    {
      name: 'sectionHeader_response',
      label: {
        en: '=== Immediate Response ===',
        th: '=== การตอบสนองทันที ==='
      },
      type: 'header'
    },
    {
      name: 'firstAidProvided',
      label: {
        en: 'First Aid Provided?',
        th: 'ให้การปฐมพยาบาลแล้วหรือยัง?'
      },
      type: 'select',
      options: [
        { value: 'yes', label: { en: 'Yes', th: 'ใช่' } },
        { value: 'no', label: { en: 'No', th: 'ไม่' } }
      ],
      required: true,
      tooltip: {
        en: 'Was first aid administered immediately after the incident? This helps determine response effectiveness. Example: Yes - Applied ice pack and bandage to minor laceration.',
        th: 'ให้การปฐมพยาบาลทันทีหลังจากเกิดเหตุการณ์หรือไม่? ช่วยกำหนดประสิทธิภาพการตอบสนอง ตัวอย่าง: ใช่ - ใช้ที่ประคบเย็นและผ้าพันแผลกับแผลฉีกเล็กน้อย'
      }
    },
    {
      name: 'medicalAttention',
      label: {
        en: 'Medical Attention Required?',
        th: 'จำเป็นต้องได้รับการรักษาทางการแพทย์หรือไม่?'
      },
      type: 'select',
      options: [
        { value: 'yes', label: { en: 'Yes', th: 'ใช่' } },
        { value: 'no', label: { en: 'No', th: 'ไม่' } }
      ],
      required: true,
      tooltip: {
        en: 'Did the incident require professional medical evaluation beyond first aid? This affects reporting requirements and insurance claims. Example: No - Minor injury treated with basic first aid.',
        th: 'เหตุการณ์ต้องการการประเมินทางการแพทย์มืออาชีพนอกเหนือจากปฐมพยาบาลหรือไม่? ส่งผลต่อข้อกำหนดการรายงานและเรียกร้องประกัน ตัวอย่าง: ไม่ - บาดเจ็บเล็กน้อยได้รับการปฐมพยาบาลเบื้องต้น'
      }
    },
    {
      name: 'hospitalVisit',
      label: {
        en: 'Hospital Visit Required?',
        th: 'จำเป็นต้องไปโรงพยาบาลหรือไม่?'
      },
      type: 'select',
      options: [
        { value: 'yes', label: { en: 'Yes', th: 'ใช่' } },
        { value: 'no', label: { en: 'No', th: 'ไม่' } }
      ],
      required: true,
      tooltip: {
        en: 'Was hospital treatment necessary? Critical for tracking severe incidents and legal compliance. Example: No - Injury did not require hospital facilities.',
        th: 'จำเป็นต้องได้รับการรักษาที่โรงพยาบาลหรือไม่? สิ่งสำคัญสำหรับการติดตามเหตุการณ์รุนแรงและการปฏิบัติตามกฎหมาย ตัวอย่าง: ไม่ - บาดเจ็บไม่จำเป็นต้องใช้สิ่งอำนวยความสะดวกของโรงพยาบาล'
      }
    },
    {
      name: 'emergencyResponse',
      label: {
        en: 'Emergency Response Activated?',
        th: 'เปิดใช้งานการตอบสนองฉุกเฉินแล้วหรือยัง?'
      },
      type: 'select',
      options: [
        { value: 'yes', label: { en: 'Yes', th: 'ใช่' } },
        { value: 'no', label: { en: 'No', th: 'ไม่' } }
      ],
      required: true,
      tooltip: {
        en: 'Were emergency response procedures initiated? Essential for major incident protocols. Example: No - Incident was minor and contained.',
        th: 'เปิดใช้งานขั้นตอนการตอบสนองฉุกเฉินหรือไม่? สิ่งสำคัญสำหรับโพรโทคอลเหตุการณ์ใหญ่ ตัวอย่าง: ไม่ - เหตุการณ์เล็กน้อยและควบคุมได้'
      }
    },
    {
      name: 'responseActions',
      label: {
        en: 'Immediate Response Actions Taken',
        th: 'การดำเนินการตอบสนองทันทีที่ดำเนินการแล้ว'
      },
      type: 'textarea',
      rows: 4,
      placeholder: {
        en: 'Describe immediate actions taken to control the situation...',
        th: 'อธิบายการดำเนินการทันทีที่ดำเนินการเพื่อควบคุมสถานการณ์...'
      },
      tooltip: {
        en: 'Chronologically list all immediate actions taken to mitigate incident impact. Include evacuation, containment, communication, and shutdown procedures. Example: 15:02 - Evacuated 5 workers from adjacent area. 15:05 - Shut off chemical supply valve. 15:10 - Notified Safety Officer.',
        th: 'ระบุการดำเนินการทันทีทั้งหมดตามลำดับเวลาที่ดำเนินการเพื่อลดผลกระทบของเหตุการณ์ รวมถึงการอพยพ การจำกัดวง การสื่อสาร และขั้นตอนการปิดเครื่อง ตัวอย่าง: 15:02 - อพยพผู้ปฏิบัติงาน 5 คนออกจากพื้นที่ใกล้เคียง 15:05 - ปิดวาล์วจ่ายสารเคมี 15:10 - แจ้งเจ้าหน้าที่ความปลอดภัย'
      }
    },

    // Equipment/Property Damage Section
    {
      name: 'sectionHeader_damage',
      label: {
        en: '=== Equipment/Property Damage ===',
        th: '=== ความเสียหายของอุปกรณ์/ทรัพย์สิน ==='
      },
      type: 'header'
    },
    {
      name: 'propertyDamage',
      label: {
        en: 'Property/Equipment Damaged',
        th: 'ทรัพย์สิน/อุปกรณ์ที่เสียหาย'
      },
      type: 'textarea',
      rows: 4,
      placeholder: {
        en: 'List damaged property/equipment, estimated costs...',
        th: 'ระบุทรัพย์สิน/อุปกรณ์ที่เสียหาย ประมาณการค่าใช้จ่าย...'
      },
      tooltip: {
        en: 'Detail all damaged property, equipment, or crops. Include item descriptions, serial numbers, age, replacement costs, and repair estimates. Example: Sprayer nozzle #SN12345, 2 years old, $150 replacement value, $50 repair estimate.',
        th: 'ระบุรายละเอียดทรัพย์สิน อุปกรณ์ หรือพืชผลที่เสียหาย รวมถึงคำอธิบายรายการ หมายเลขซีเรียล อายุ ค่าใช้จ่ายในการแทนที่ และค่าประมาณการซ่อม ตัวอย่าง: หัวพ่นยา #SN12345 อายุ 2 ปี มูลค่าแทนที่ $150 ค่าประมาณการซ่อม $50'
      }
    },
    {
      name: 'productionImpact',
      label: {
        en: 'Impact on Production',
        th: 'ผลกระทบต่อการผลิต'
      },
      type: 'textarea',
      rows: 3,
      placeholder: {
        en: 'Describe impact on production schedule...',
        th: 'อธิบายผลกระทบต่อตารางการผลิต...'
      },
      tooltip: {
        en: 'Estimate production delays, yield losses, or schedule disruptions. Include timelines and quantifiable impacts. Example: Field Block B harvest delayed 3 days, estimated 2-ton yield loss.',
        th: 'ประมาณการความล่าช้าในการผลิต การสูญเสียผลผลิต หรือการรบกวนกำหนดการ รวมถึงระยะเวลาและผลกระทบที่สามารถวัดปริมาณได้ ตัวอย่าง: การเก็บเกี่ยวพื้นที่บล็อก B ล่าช้า 3 วัน ประมาณการสูญเสียผลผลิต 2 ตัน'
      }
    },

    // Investigation Section
    {
      name: 'sectionHeader_investigation',
      label: {
        en: '=== Investigation ===',
        th: '=== การสอบสวน ==='
      },
      type: 'header'
    },
    {
      name: 'investigatorAssigned',
      label: {
        en: 'Investigator Assigned',
        th: 'ผู้สอบสวนที่ได้รับมอบหมาย'
      },
      type: 'text',
      tooltip: {
        en: 'Name and title of the person assigned to lead the incident investigation. Example: Dr. Michael Chen, Safety Director',
        th: 'ชื่อและตำแหน่งของบุคคลที่ได้รับมอบหมายให้เป็นผู้นำการสอบสวนเหตุการณ์ ตัวอย่าง: ดร. ไมเคิล เชน ผู้อำนวยการด้านความปลอดภัย'
      }
    },
    {
      name: 'investigationTargetDate',
      label: {
        en: 'Investigation Target Completion Date',
        th: 'วันที่กำหนดเสร็จของการสอบสวน'
      },
      type: 'date',
      tooltip: {
        en: 'Deadline for completing the full incident investigation. Typically within 7 business days. Example: 2023-10-22',
        th: 'กำหนดเวลาสำหรับการสอบสวนเหตุการณ์ให้เสร็จสมบูรณ์ โดยทั่วไปภายใน 7 วันทำการ ตัวอย่าง: 2023-10-22'
      }
    },
    {
      name: 'findings',
      label: {
        en: 'Investigation Findings',
        th: 'ผลการสอบสวน'
      },
      type: 'textarea',
      rows: 5,
      placeholder: {
        en: 'Detailed findings from the investigation...',
        th: 'ผลการสอบสวนโดยละเอียด...'
      },
      tooltip: {
        en: 'Comprehensive documentation of investigation results. Include witness statements, photographic evidence, expert opinions, and technical analysis. Example: Root cause identified as inadequate lockout/tagout procedures during equipment maintenance.',
        th: 'เอกสารประกอบผลการสอบสวนอย่างครอบคลุม รวมถึงคำให้การของพยาน หลักฐานภาพ ความเห็นของผู้เชี่ยวชาญ และการวิเคราะห์ทางเทคนิค ตัวอย่าง: ระบุสาเหตุรากฐานว่าเป็นขั้นตอนการล็อค/ติดป้ายไม่เพียงพอในระหว่างการบำรุงรักษาอุปกรณ์'
      }
    },

    // Corrective Actions Section
    {
      name: 'sectionHeader_corrective',
      label: {
        en: '=== Corrective Actions ===',
        th: '=== มาตรการแก้ไข ==='
      },
      type: 'header'
    },
    {
      name: 'correctiveActions',
      label: {
        en: 'Corrective Actions to Prevent Recurrence',
        th: 'มาตรการแก้ไขเพื่อป้องกันการเกิดซ้ำ'
      },
      type: 'textarea',
      rows: 6,
      placeholder: {
        en: 'List specific corrective actions with responsible persons and due dates...',
        th: 'ระบุมาตรการแก้ไขเฉพาะพร้อมบุคคลที่รับผิดชอบและวันครบกำหนด...'
      },
      tooltip: {
        en: 'Detail specific, actionable steps to prevent similar incidents. Include SMART goals (Specific, Measurable, Achievable, Relevant, Time-bound). Assign responsible persons and set realistic deadlines. Example: Revise lockout/tagout SOP by Oct 30, 2023. Train all maintenance staff by Nov 15, 2023.',
        th: 'ระบุขั้นตอนเฉพาะที่สามารถดำเนินการได้เพื่อป้องกันเหตุการณ์ที่คล้ายกัน รวมถึงเป้าหมาย SMART (เฉพาะเจาะจง วัดผลได้ ทำได้จริง เกี่ยวข้อง กำหนดเวลา) มอบหมายบุคคลที่รับผิดชอบและกำหนดระยะเวลาที่เป็นจริง ตัวอย่าง: แก้ไขขั้นตอนการล็อค/ติดป้าย SOP ภายใน 30 ต.ค. 2023 ฝึกอบรมบุคลากรบำรุงรักษาทั้งหมดภายใน 15 พ.ย. 2023'
      }
    },
    {
      name: 'preventiveActions',
      label: {
        en: 'Preventive Actions to Avoid Similar Incidents',
        th: 'มาตรการป้องกันเพื่อหลีกเลี่ยงเหตุการณ์ที่คล้ายกัน'
      },
      type: 'textarea',
      rows: 4,
      placeholder: {
        en: 'List preventive measures to avoid similar incidents...',
        th: 'ระบุมาตรการป้องกันเพื่อหลีกเลี่ยงเหตุการณ์ที่คล้ายกัน...'
      },
      tooltip: {
        en: 'Identify broader systemic improvements beyond immediate fixes. Include policy changes, training enhancements, technology upgrades, or procedural improvements. Example: Implement quarterly safety culture surveys to identify emerging risks proactively.',
        th: 'ระบุการปรับปรุงระบบโดยรวมนอกเหนือจากการแก้ไขทันที รวมถึงการเปลี่ยนแปลงนโยบาย การปรับปรุงการฝึกอบรม การปรับปรุงเทคโนโลยี หรือการปรับปรุงขั้นตอน ตัวอย่าง: ดำเนินการสำรวจวัฒนธรรมความปลอดภัยรายไตรมาสเพื่อระบุความเสี่ยงที่เกิดขึ้นล่วงหน้า'
      }
    },

    // Follow-up Section
    {
      name: 'sectionHeader_followup',
      label: {
        en: '=== Follow-up ===',
        th: '=== การติดตามผล ==='
      },
      type: 'header'
    },
    {
      name: 'followUpDate',
      label: {
        en: 'Follow-up Review Date',
        th: 'วันที่ทบทวนผลการติดตาม'
      },
      type: 'date',
      tooltip: {
        en: 'Scheduled date for reviewing effectiveness of corrective actions. Example: 2023-11-30',
        th: 'วันที่กำหนดสำหรับการทบทวนประสิทธิภาพของมาตรการแก้ไข ตัวอย่าง: 2023-11-30'
      }
    },
    {
      name: 'effectiveness',
      label: {
        en: 'Effectiveness of Corrective Actions',
        th: 'ประสิทธิภาพของมาตรการแก้ไข'
      },
      type: 'textarea',
      rows: 3,
      placeholder: {
        en: 'Assess effectiveness of implemented corrective actions...',
        th: 'ประเมินประสิทธิภาพของมาตรการแก้ไขที่ดำเนินการแล้ว...'
      },
      tooltip: {
        en: 'Evaluate whether corrective actions successfully prevented recurrence. Include measurable outcomes and lessons learned. Example: No similar incidents in 60 days since SOP revision. Staff compliance improved from 65% to 95%.',
        th: 'ประเมินว่ามาตรการแก้ไขป้องกันการเกิดซ้ำสำเร็จหรือไม่ รวมถึงผลลัพธ์ที่สามารถวัดได้และบทเรียนที่ได้เรียนรู้ ตัวอย่าง: ไม่มีเหตุการณ์ที่คล้ายกันใน 60 วันนับแต่การแก้ไข SOP ความปฏิบัติตามของบุคลากรปรับปรุงจาก 65% เป็น 95%'
      }
    },
    {
      name: 'closureRemarks',
      label: {
        en: 'Closure Remarks',
        th: 'หมายเหตุปิดเคส'
      },
      type: 'textarea',
      rows: 3,
      placeholder: {
        en: 'Final remarks and case closure notes...',
        th: 'หมายเหตุสุดท้ายและบันทึกการปิดเคส...'
      },
      tooltip: {
        en: 'Concluding observations and recommendations for continuous improvement. Summarize key takeaways and organizational impact. Example: Case closed - SOP updates implemented company-wide. Recommend quarterly refresher training sessions.',
        th: 'ข้อสังเกตและการแนะนำสุดท้ายสำหรับการปรับปรุงอย่างต่อเนื่อง สรุปข้อคิดสำคัญและผลกระทบต่อองค์กร ตัวอย่าง: ปิดเคส - การอัปเดต SOP ดำเนินการทั่วทั้งบริษัท แนะนำการฝึกอบรมทบทวนรายไตรมาส'
      }
    }
  ]
});

export default getIncidentReportFormTemplate;